import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { Filter, Download, Printer } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { operatorRequiresValue } from '../filters/operators';

function Toolbar({
  searchQuery,
  setSearchQuery,
  view,
  setView,
  colors,
  isSmallScreen,
  // Export function dependencies
  columns,
  filteredData,
  getNestedValue,
  formatLabel,
  userData,
  tableName,
  exportTitle,
  recordKey,
  recordLabel,
  // Search dependencies
  updateURL,
  setCurrentPage,
  // Filter dependencies
  loadedBackendConfig,
  convertToFilterBuilderFormat,
  setPendingFilters,
  setPendingFilterBuilderFilters,
  setLockedFilters,
  setShowFilters,
  handleFilterDialogOpen: handleFilterDialogOpenProp,
  // Print dependencies
  data,
  searchPlaceholder = "Search...",
  hideViewToggle = false,
  hideFilterButton = false,
  hidePersonalizeView = false,
  centerContent = null,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const searchDebounceRef = useRef(null);
  const resolvedRecordKey = recordKey || "id";
  const resolvedRecordLabel = recordLabel || "Record";
  const resolvedExportTitle = exportTitle || tableName || "List";

  // Export to Excel
  const exportToExcel = useCallback(() => {
    const visibleColumns = columns.filter((col) => col.visible);
    const organizationName =
      userData?.organization?.organization_name || "KS Travel Report";
    const generatedBy = userData?.user?.name || "System User";
    const generatedDate = new Date().toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
    });

    const dataForExcel = filteredData.map((row) => {
      const newRow = {};
      visibleColumns.forEach((col) => {
        newRow[col.label] = getNestedValue(row, col.key);
      });
      return newRow;
    });

    const headerRows = [
      { A: organizationName },
      { A: resolvedExportTitle },
      { A: `Generated by: ${generatedBy}` },
      { A: `Generated on: ${generatedDate}` },
      {},
    ];

    const ws = XLSX.utils.json_to_sheet([], { skipHeader: true });
    XLSX.utils.sheet_add_json(ws, headerRows, { skipHeader: true, origin: "A1" });
    XLSX.utils.sheet_add_json(ws, dataForExcel, { origin: -1 });

    const columnWidths = visibleColumns.map((col) => {
      const headerText = col.label || "";
      const maxDataLength = filteredData.reduce((max, row) => {
        const cellValue = String(getNestedValue(row, col.key) || "");
        return Math.max(max, cellValue.length);
      }, 0);
      return { wch: Math.max(headerText.length, maxDataLength) + 2 };
    });

    ws["!cols"] = columnWidths;

    const boldRows = [1, 2];
    boldRows.forEach((r) => {
      const cellAddress = `A${r}`;
      if (ws[cellAddress]) {
        ws[cellAddress].s = { font: { bold: true, sz: 14 } };
      }
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tableName}_${generatedDate}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
    setExportMenuAnchorEl(null);
  }, [columns, filteredData, getNestedValue, userData, tableName, resolvedExportTitle]);

  // Export to CSV
  const exportToCSV = useCallback(() => {
    const visibleColumns = columns.filter((col) => col.visible);
    const organizationName =
      userData?.organization?.organization_name || "Nest Software Private Limited";
    const generatedBy = userData?.user_name || "System User";
    const generatedOn = new Date().toLocaleDateString();

    const metadata = [
      [`${organizationName}`],
      [`${resolvedExportTitle}`],
      [`Generated By: ${generatedBy}`],
      [`Generated On: ${generatedOn}`],
      [],
    ];

    const headers = [visibleColumns.map((col) => col.label)];
    const rows = filteredData.map((row) =>
      visibleColumns.map((col) =>
        String(getNestedValue(row, col.key) ?? "").replace(/"/g, '""')
      )
    );

    const allData = [...metadata, ...headers, ...rows];
    const csvContent = allData
      .map((row) => row.map((v) => `"${v}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tableName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExportMenuAnchorEl(null);
  }, [columns, filteredData, getNestedValue, tableName, userData, resolvedExportTitle]);

  // Export to HTML
  const exportToHTML = useCallback(() => {
    const visibleColumns = columns.filter((col) => col.visible);
    const organizationName =
      userData?.organization?.organization_name || "KS Travel Report";
    const generatedDate = new Date().toLocaleDateString();

    const recordsHTML = filteredData
      .map(
        (row) =>
          `<div style="margin-bottom: 30px; page-break-inside: avoid;">
          <h3 style="color: #333; margin-bottom: 15px; font-size: 16px; font-weight: bold;">${resolvedRecordLabel} ${row[resolvedRecordKey] ?? row.id ?? "N/A"}</h3>
          <table style="border-collapse: collapse; width: 100%; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <tbody>
              ${visibleColumns
                .map(
                  (col) =>
                    `<tr>
                      <td style="border: 1px solid #ddd; padding: 12px; background-color: #f5f5f5; font-weight: bold; width: 30%;">${formatLabel(col.label)}</td>
                      <td style="border: 1px solid #ddd; padding: 12px; width: 70%;">${String(getNestedValue(row, col.key) || "")}</td>
                    </tr>`
                )
                .join("")}
            </tbody>
          </table>
        </div>`
      )
      .join("");

    const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <title>${organizationName}</title>
  <style>
    body { font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif; margin: 20px; }
    h1 { color: #333333; margin-bottom: 5px; font-size: 24px; }
    h2 { color: #333333; margin-bottom: 15px; font-size: 18px; font-weight: normal; }
    .info { color: #666666; margin-bottom: 30px; font-size: 14px; }
    .info-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
  </style>
</head>
<body>
  <h1>${organizationName}</h1>
  <h2>${resolvedExportTitle}</h2>
  <div class="info">
    <div class="info-row">
      <span>Generated on: ${generatedDate}</span>
      <span>Total Records: ${filteredData.length}</span>
    </div>
  </div>
  ${recordsHTML}
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tableName}.html`;
    a.click();
    URL.revokeObjectURL(url);
    setExportMenuAnchorEl(null);
  }, [columns, filteredData, getNestedValue, formatLabel, userData, resolvedRecordKey, resolvedRecordLabel, tableName, resolvedExportTitle]);

  // Export to PDF
  const exportToPDF = useCallback(async () => {
    try {
      const jsPDFModule = await import("jspdf");
      const jsPDF = jsPDFModule.default;
      const doc = new jsPDF();
      const visibleColumns = columns.filter((col) => col.visible);

      if (visibleColumns.length === 0) {
        alert("No visible columns to export. Please manage columns first.");
        setExportMenuAnchorEl(null);
        return;
      }

      if (filteredData.length === 0) {
        alert("No data to export after applying filters.");
        setExportMenuAnchorEl(null);
        return;
      }

      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 14;
      const usableWidth = pageWidth - 2 * margin;
      const cellPadding = 2;
      const lineHeight = doc.getLineHeight() / doc.internal.scaleFactor;
      const minCellHeight = 8;
      let currentY = 40;

      const keyColumnWidth = (() => {
        let maxWidth = 0;
        visibleColumns.forEach((col) => {
          const cleanedLabel = col.label.replace(/_/g, " ");
          maxWidth = Math.max(maxWidth, doc.getTextWidth(cleanedLabel));
        });
        return Math.min(maxWidth + cellPadding * 2, usableWidth * 0.3);
      })();

      const valueColumnWidth = usableWidth - keyColumnWidth;

      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      const organizationName =
        userData?.organization?.organization_name || "KS Travel Report";
      doc.text(organizationName, margin, 20);

      doc.setFontSize(14);
      doc.setFont(undefined, "normal");
      doc.text(resolvedExportTitle, margin, 30);

      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, currentY);
      doc.text(`Total Records: ${filteredData.length}`, pageWidth - margin - 50, currentY);
      currentY += 15;

      filteredData.forEach((dataRow, recordIndex) => {
        const recordTitleHeight = 14;
        const spacingBeforeTitle = 15;
        const spacingAfterTitle = 10;
        const spacingAfterTable = 15;

        let recordBlockContentHeight = 0;
        visibleColumns.forEach((col) => {
          const value = String(getNestedValue(dataRow, col.key) || "");
          const cleanedValue = value.replace(/_/g, " ");
          const valueLines = doc.splitTextToSize(cleanedValue, valueColumnWidth - cellPadding * 2);
          const rowHeight = Math.max(minCellHeight, valueLines.length * lineHeight + cellPadding * 2);
          recordBlockContentHeight += rowHeight;
        });

        const totalRecordBlockHeight =
          spacingBeforeTitle + recordTitleHeight + spacingAfterTitle +
          recordBlockContentHeight + spacingAfterTable;

        if (recordIndex > 0 && currentY + totalRecordBlockHeight > pageHeight - margin) {
          doc.addPage();
          currentY = margin;
        } else if (recordIndex === 0 && currentY + totalRecordBlockHeight > pageHeight - margin && currentY > margin) {
          doc.addPage();
          currentY = margin;
        }

        if (currentY > margin) currentY += spacingBeforeTitle;

        doc.setFontSize(14);
        doc.setFont(undefined, "bold");
        const recordId = dataRow[resolvedRecordKey] ?? dataRow.id ?? "N/A";
        const recordTitle = `${resolvedRecordLabel} ${recordId}`;
        doc.text(recordTitle, margin, currentY);
        currentY += recordTitleHeight + spacingAfterTitle;

        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        doc.setDrawColor(0);

        visibleColumns.forEach((col) => {
          const cleanedLabel = col.label.replace(/_/g, " ").replace(/([A-Z])/g, " $1").replace(/\s+/g, " ").trim();
          const formattedLabel = cleanedLabel.charAt(0).toUpperCase() + cleanedLabel.slice(1);

          const value = String(getNestedValue(dataRow, col.key) || "").replace(/_/g, " ").trim();
          const valueLines = doc.splitTextToSize(value, valueColumnWidth - cellPadding * 2);
          const rowHeight = Math.max(minCellHeight, valueLines.length * lineHeight + cellPadding * 2);

          doc.rect(margin, currentY, keyColumnWidth, rowHeight, "S");
          const labelLines = doc.splitTextToSize(formattedLabel, keyColumnWidth - cellPadding * 2);
          const totalLabelHeight = labelLines.length * lineHeight;
          const labelYOffset = (rowHeight - totalLabelHeight) / 2;
          labelLines.forEach((line, lineIndex) => {
            doc.text(line, margin + cellPadding, currentY + labelYOffset + lineHeight * lineIndex, { baseline: "top", align: "left" });
          });

          doc.rect(margin + keyColumnWidth, currentY, valueColumnWidth, rowHeight, "S");
          const totalValueHeight = valueLines.length * lineHeight;
          const valueYOffset = (rowHeight - totalValueHeight) / 2;
          valueLines.forEach((line, lineIndex) => {
            doc.text(line, margin + keyColumnWidth + cellPadding, currentY + valueYOffset + lineHeight * lineIndex, { baseline: "top", align: "left" });
          });

          currentY += rowHeight;
        });

        currentY += spacingAfterTable;
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 30, pageHeight - 10);
        doc.text(`${filteredData.length} records exported`, margin, pageHeight - 10);
      }

      doc.save(`${tableName}.pdf`);
      setExportMenuAnchorEl(null);
    } catch (error) {
      console.error("PDF export failed:", error);
      alert("PDF export failed. Please try again.");
    }
  }, [columns, filteredData, getNestedValue, formatLabel, userData, resolvedRecordKey, resolvedRecordLabel, tableName, resolvedExportTitle]);

  // Handle search change with debounce
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      updateURL({ searchQuery: value, currentPage: 1 });
      setCurrentPage(1);
    }, 500);
  }, [setSearchQuery, updateURL, setCurrentPage]);

  // Handle view change
  const handleViewChange = useCallback((newView) => {
    setView(newView);
  }, [setView]);

  // Handle filter dialog open (internal fallback if no prop handler)
  const handleFilterDialogOpenInternal = useCallback(() => {
    let initialFilters = {};
    let readOnlyFilters = {};

    if (loadedBackendConfig?.filters) {
      initialFilters = loadedBackendConfig.filters;
    } else {
      searchParams.forEach((value, key) => {
        if (key.startsWith("filter_") && !key.endsWith("_operator") && !key.endsWith("_label")) {
          const filterKey = key.replace("filter_", "");
          const operatorKey = `filter_${filterKey}_operator`;
          const operator = searchParams.get(operatorKey);
          const requiresValue = operator ? operatorRequiresValue(operator) : true;
          if (value || !requiresValue) {
            initialFilters[filterKey] = value || '';
            readOnlyFilters[filterKey] = true;
          }
        }
      });
    }

    const filterBuilderFilters = convertToFilterBuilderFormat(initialFilters);
    setPendingFilters(initialFilters);
    setPendingFilterBuilderFilters(filterBuilderFilters);
    setLockedFilters(readOnlyFilters);
    setShowFilters(true);
  }, [loadedBackendConfig, searchParams, convertToFilterBuilderFormat, setPendingFilters, setPendingFilterBuilderFilters, setLockedFilters, setShowFilters]);

  const handleFilterDialogOpen = handleFilterDialogOpenProp || handleFilterDialogOpenInternal;

  // Handle print
  const handlePrint = useCallback(() => {
    const printStyles = document.createElement("style");
    printStyles.id = "table-print-styles";
    printStyles.innerHTML = `
    @media print {
      body * { visibility: hidden; }
      .custom-print-container, .custom-print-container * { visibility: visible; }
      .custom-print-container {
        position: absolute; left: 0; top: 0; width: 100%;
        font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4;
        color: #000; background: white; padding: 20px; box-sizing: border-box;
      }
      @page { size: A4; margin: 15mm; }
      .print-header { text-align: left; margin-bottom: 30px; }
      .print-title { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
      .print-subtitle { font-size: 14px; margin-bottom: 15px; }
      .print-meta { display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 20px; }
      .record-section { margin-bottom: 30px; page-break-inside: avoid; }
      .record-title { font-size: 14px; font-weight: bold; margin-bottom: 15px; }
      .record-table { width: 100%; border-collapse: collapse; border: 2px solid #000; margin-bottom: 20px; }
      .record-table td { border: 1px solid #000; padding: 8px 12px; vertical-align: top; }
      .record-table .field-name { background-color: #f0f0f0; font-weight: bold; width: 30%; -webkit-print-color-adjust: exact; color-adjust: exact; }
      .record-table .field-value { width: 70%; }
      .print-footer { position: fixed; bottom: 10mm; left: 0; right: 0; text-align: center; font-size: 10px; }
    }
  `;
    document.head.appendChild(printStyles);

    const currentDate = new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
    const tableData = data || [];
    const totalRecords = tableData.length;
    const printContent = document.createElement("div");
    printContent.className = "custom-print-container";

    const header = document.createElement("div");
    header.className = "print-header";
    const organizationName = userData?.organization?.organization_name || "KS Travel";
    header.innerHTML = `
    <div class="print-title">${organizationName}</div>
    <div class="print-subtitle">${resolvedExportTitle}</div>
    <div class="print-meta">
      <span>Generated on: ${currentDate}</span>
      <span>Total Records: ${totalRecords}</span>
    </div>
  `;
    printContent.appendChild(header);

    const visibleColumns = columns.filter((col) => col.visible).map((col) => col.key);

    tableData.forEach((row) => {
      const recordSection = document.createElement("div");
      recordSection.className = "record-section";
      const recordId = row[resolvedRecordKey] ?? row.id ?? "N/A";
      const recordTitle = document.createElement("div");
      recordTitle.className = "record-title";
      recordTitle.textContent = `${resolvedRecordLabel} ${recordId}`;
      recordSection.appendChild(recordTitle);

      const recordTable = document.createElement("table");
      recordTable.className = "record-table";

      visibleColumns.forEach((key) => {
        if (key.startsWith("_")) return;
        const value = row[key];
        const tableRow = document.createElement("tr");
        const fieldNameCell = document.createElement("td");
        fieldNameCell.className = "field-name";
        const cleanedKey = key.replace(/_/g, " ").replace(/([A-Z])/g, " $1").replace(/\s+/g, " ").trim();
        fieldNameCell.textContent = cleanedKey.charAt(0).toUpperCase() + cleanedKey.slice(1);
        const fieldValueCell = document.createElement("td");
        fieldValueCell.className = "field-value";
        fieldValueCell.textContent = (value || "").toString().replace(/_/g, " ");
        tableRow.appendChild(fieldNameCell);
        tableRow.appendChild(fieldValueCell);
        recordTable.appendChild(tableRow);
      });

      recordSection.appendChild(recordTable);
      printContent.appendChild(recordSection);
    });

    const footer = document.createElement("div");
    footer.className = "print-footer";
    footer.innerHTML = `${totalRecords} records exported<span style="float: right;">Page 1 of 2</span>`;
    printContent.appendChild(footer);
    document.body.appendChild(printContent);
    window.print();

    setTimeout(() => {
      document.body.removeChild(printContent);
      const printStylesElement = document.getElementById("table-print-styles");
      if (printStylesElement) printStylesElement.remove();
    }, 1000);
  }, [data, userData, columns, tableName, resolvedRecordKey, resolvedRecordLabel, resolvedExportTitle]);

  // Handle personalize layout navigation
  const handlePersonalizeLayoutClick = useCallback(() => {
    navigate("personalize-view");
  }, [navigate]);

  // Handle export menu open
  const handleExportClick = useCallback((event) => {
    setExportMenuAnchorEl(event.currentTarget);
  }, []);

  // Cleanup search debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  return (
    <>
      <div className="ems-card ems-dt-toolbar">
        {/* Search */}
        <div className="ems-dt-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            strokeLinejoin="round" style={{ flexShrink: 0, opacity: .5 }}>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>

        {centerContent ? (
          <div className="ems-dt-center">{centerContent}</div>
        ) : null}

        {/* Spacer — hidden on small screens so filters can take a full row */}
        <div className="ems-dt-spacer" />

        {/* View toggle — table is impractical on phones; cards are forced there */}
        {!hideViewToggle && !isSmallScreen && (
          <div className="ems-seg icons">
            <button
              className={view === 'table' ? 'active' : ''}
              onClick={() => handleViewChange('table')}
              aria-label="Table view"
              title="Table view"
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <button
              className={view === 'card' ? 'active' : ''}
              onClick={() => handleViewChange('card')}
              aria-label="Card view"
              title="Card view"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
              </svg>
            </button>
          </div>
        )}

        {!isSmallScreen && (!hideViewToggle || !hideFilterButton) && <div className="ems-dt-sep" />}

        {/* Icon buttons */}
        <div className="ems-dt-tools">
          {!hideFilterButton && (
            <button className="ems-dt-iconbtn" onClick={handleFilterDialogOpen} title="Filters" aria-label="Filters">
              <Filter size={17} />
            </button>
          )}
          <button className="ems-dt-iconbtn" onClick={handleExportClick} title="Export" aria-label="Export">
            <Download size={17} />
          </button>
          <button className="ems-dt-iconbtn" onClick={handlePrint} title="Print" aria-label="Print">
            <Printer size={17} />
          </button>
        </div>

        {!hidePersonalizeView && !isSmallScreen && (
          <button className="ems-dt-personalize" onClick={handlePersonalizeLayoutClick}>
            Personalize View
          </button>
        )}
      </div>

      {/* Export dropdown — keep MUI for positioning */}
      <Menu
        anchorEl={exportMenuAnchorEl}
        open={Boolean(exportMenuAnchorEl)}
        onClose={() => setExportMenuAnchorEl(null)}
      >
        <MenuItem onClick={exportToExcel}>Export to XLS</MenuItem>
        <MenuItem onClick={exportToPDF}>Export to PDF</MenuItem>
        <MenuItem onClick={exportToHTML}>Export to HTML</MenuItem>
        <MenuItem onClick={exportToCSV}>Export to CSV</MenuItem>
      </Menu>
    </>
  );
}

export default Toolbar;
